import type { FC, RefAttributes } from "react";
import { Slot } from "@/components/ui/composition-slot";
import type { ButtonProps } from "./button";
import { cn } from "@/lib/utils";
import type { JSXElementConstructor, ReactElement } from "react";
import { cloneElement, useMemo } from "react";
import { buttonVariants } from "./variants/button-variants";

export interface IconButtonProps extends ButtonProps {
  icon: ReactElement<{ className: string }, string | JSXElementConstructor<unknown>>;
}

export const IconButton: FC<IconButtonProps & RefAttributes<HTMLButtonElement>> = ({
  ref,
  className,
  variant,
  size,
  icon,
  asChild = false,
  children,
  ...props
}) => {
  const Comp = asChild ? Slot : "button";

  const iconMarginClass = useMemo(() => {
    if (children) {
      switch (size) {
        case "xs":
        case "sm":
          return "mr-1.5";
        case "lg":
          return "mr-3";
        case "default":
        default:
          return "mr-2";
      }
    } else {
      return "";
    }
  }, [children, size]);

  const iconSizeClass = useMemo(() => {
    switch (size) {
      case "xs":
        return "w-3 h-3";
      case "lg":
        return "w-5 h-5";
      case "sm":
      case "default":
      default:
        return "w-4 h-4";
    }
  }, [size]);

  return (
    <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
      {cloneElement(icon, { className: cn(iconSizeClass, iconMarginClass, icon.props.className) })}
      {children}
    </Comp>
  );
};

IconButton.displayName = "IconButton";
